import { forwardRef, type HTMLAttributes } from 'react';
import { cn, getPercentageColor, getPercentageColorText } from '../lib/utils';

export interface ProgressBarProps extends HTMLAttributes<HTMLDivElement> {
  value: number;
  max: number;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  label?: string;
  showPercentage?: boolean;
  variant?: 'default' | 'color-coded';
}

export const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    {
      className,
      value,
      max,
      size = 'md',
      showLabel = false,
      label,
      showPercentage = true,
      variant = 'default',
      ...props
    },
    ref
  ) => {
    const percentage = Math.min(Math.round((value / max) * 100), 100);

    const sizeStyles = {
      sm: 'h-1.5',
      md: 'h-2.5',
      lg: 'h-4',
    };

    const colorClass =
      variant === 'color-coded'
        ? getPercentageColor(percentage).replace('bg-', 'bg-').replace('error', 'red-500').replace('warning', 'amber-500').replace('info', 'blue-500').replace('success', 'emerald-500')
        : 'bg-primary-500';

    return (
      <div ref={ref} className={cn('w-full', className)} {...props}>
        {(showLabel || label) && (
          <div className="flex justify-between items-center mb-1">
            {label && <span className="text-sm font-medium text-slate-700">{label}</span>}
            {showPercentage && (
              <span className={cn('text-sm font-medium', getPercentageColorText(percentage))}>
                {value}/{max} ({percentage}%)
              </span>
            )}
          </div>
        )}
        <div className={cn('w-full bg-slate-200 rounded-full overflow-hidden', sizeStyles[size])}>
          <div
            className={cn('h-full transition-all duration-500 ease-out', colorClass)}
            style={{ width: `${Math.max(percentage, 0)}%` }}
            data-testid="progress-fill"
          />
        </div>
      </div>
    );
  }
);

ProgressBar.displayName = 'ProgressBar';

export interface QuotaProgressBarProps extends Omit<ProgressBarProps, 'variant'> {
  resetsAt?: string;
  cancelled?: boolean;  actionLabel?: string;
  onActionClick?: () => void;
}

export const QuotaProgressBar = forwardRef<HTMLDivElement, QuotaProgressBarProps>(
    ({ className, value, max, label, resetsAt, cancelled, actionLabel, onActionClick, ...props }, ref) => {
    const percentage = Math.min(Math.round((value / max) * 100), 100);
    const isLow = percentage >= 90;

    return (
      <div ref={ref} className={cn('w-full', className)} {...props}>
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-slate-700">{label}</span>
          <span className={cn('text-sm font-medium', getPercentageColorText(percentage))}>
            {value}/{max}
          </span>
        </div>
        <ProgressBar value={value} max={max} variant="color-coded" />
        <div className="flex justify-between items-center mt-1">
          {resetsAt && (
            <span className="text-xs text-slate-500">
              {cancelled ? 'Termina em' : 'Reinicia em'} {new Date(resetsAt).toLocaleDateString('pt-BR')}
            </span>
          )}
          {actionLabel && (
            <button
              onClick={onActionClick}
              className={cn('text-xs font-medium underline', isLow ? 'text-red-500' : 'text-primary-500')}
            >
              {actionLabel}
            </button>
          )}
        </div>
      </div>
    );
  }
);

QuotaProgressBar.displayName = 'QuotaProgressBar';
